// Dual-Space Coach Panel
// Chat UI with markdown rendering, clickable suggestion pills, and proper scrolling

import { useState, useRef, useEffect, useMemo, useCallback } from 'react'
import { Send, Loader2, Link, AlertTriangle, Sparkles, ArrowRight } from 'lucide-react'
import { useDualSpaceCoach, type CoachMessage, type SuggestedLink } from '@/hooks/useDualSpaceCoach'
import { parseMarkdownBlocks, inlineMd, extractSuggestionPills } from './coachFormatting'
import type { SpaceNode, DualSpaceData } from '@ispot/shared'

interface DualSpaceCoachPanelProps {
  accountId: string
  projectId: string
  selectedNode: SpaceNode | null
  data: DualSpaceData | null
  apiKey: string
  onAcceptLink: (data: { sourceType: string; sourceId: string; targetType: string; targetId: string }) => void
  onCreateNeed?: (personaSessionId: string, jtbdId: string, statement: string) => void
}

const QUICK_PROMPTS = [
  { label: 'Find gaps', prompt: 'What structural gaps exist in my problem-solution mapping?' },
  { label: 'Suggest links', prompt: 'Suggest cross-links for my unlinked entities.' },
  { label: 'Value prop', prompt: 'Generate a value proposition from my strongest link chain.' },
  { label: 'Synthesize needs', prompt: 'Help me cluster related pains into customer needs.' },
]

export function DualSpaceCoachPanel({
  accountId, projectId, selectedNode, data, apiKey, onAcceptLink, onCreateNeed,
}: DualSpaceCoachPanelProps) {
  const { messages, isStreaming, error, sendMessage } = useDualSpaceCoach(accountId, projectId)
  const [input, setInput] = useState('')
  const scrollRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (scrollRef.current) {
      requestAnimationFrame(() => {
        scrollRef.current?.scrollTo({ top: scrollRef.current!.scrollHeight, behavior: 'smooth' })
      })
    }
  }, [messages, isStreaming])

  const handleSend = () => {
    if (!input.trim() || isStreaming || !apiKey) return
    const selected = selectedNode ? { id: selectedNode.id, type: selectedNode.type, title: selectedNode.title } : undefined
    sendMessage(input.trim(), apiKey, selected)
    setInput('')
  }

  const handleQuickPrompt = (prompt: string) => {
    if (isStreaming || !apiKey) return
    const selected = selectedNode ? { id: selectedNode.id, type: selectedNode.type, title: selectedNode.title } : undefined
    sendMessage(prompt, apiKey, selected)
  }

  const nextStepPills = useMemo(() => {
    if (isStreaming || messages.length === 0) return []
    const lastMsg = [...messages].reverse().find(m => m.role === 'assistant')
    return lastMsg ? extractSuggestionPills(lastMsg.content) : []
  }, [messages, isStreaming])

  /** Resolve entity names from coach text to IDs, then create the actual link */
  const tryCreateLinkByName = useCallback((sourceName: string, targetName: string) => {
    if (!data) return
    const allNodes = [...flattenNodes(data.problemSpace), ...flattenNodes(data.solutionSpace)]
    const source = findBestMatch(allNodes, sourceName)
    const target = findBestMatch(allNodes, targetName)
    if (source && target) {
      onAcceptLink({ sourceType: source.type, sourceId: source.id, targetType: target.type, targetId: target.id })
    } else {
      handleQuickPrompt(`I want to link "${sourceName}" to "${targetName}" but couldn't match the entities. Can you help?`)
    }
  }, [data, onAcceptLink, handleQuickPrompt])

  /** Create a need from the coach's suggestion */
  const tryCreateNeed = useCallback((statement: string) => {
    if (!data || !onCreateNeed) return
    // Find the first persona + first JTBD to attach the need to
    const persona = data.problemSpace[0]
    const jtbd = persona?.children.find(c => c.type === 'jtbd')
    if (persona && jtbd) {
      onCreateNeed(persona.id, jtbd.id, statement)
    }
  }, [data, onCreateNeed])

  /** Dispatch pill click by action type */
  const handlePillClick = useCallback((pill: { action: string; source?: string; target?: string; needStatement?: string; prompt: string }) => {
    if (pill.action === 'link' && pill.source && pill.target) {
      tryCreateLinkByName(pill.source, pill.target)
    } else if (pill.action === 'need' && pill.needStatement) {
      tryCreateNeed(pill.needStatement)
    } else {
      handleQuickPrompt(pill.prompt)
    }
  }, [tryCreateLinkByName, tryCreateNeed, handleQuickPrompt])

  return (
    <div className="flex flex-col h-full min-h-0">
      {/* Messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto min-h-0 px-3 py-3 space-y-3">
        {messages.length === 0 && <EmptyState onQuickPrompt={handleQuickPrompt} disabled={isStreaming || !apiKey} />}
        {messages.map(msg => (
          <MessageBubble key={msg.id} message={msg} onAcceptLink={onAcceptLink} onCreateLink={tryCreateLinkByName} onFollowUp={handleQuickPrompt} />
        ))}
        {isStreaming && (
          <div className="flex items-center gap-2 text-[12px] text-text-muted py-1">
            <Loader2 size={12} className="animate-spin" /> Thinking...
          </div>
        )}
        {error && <div className="text-[12px] text-danger bg-danger/5 px-3 py-2 rounded-md">{error}</div>}
      </div>

      {/* Next step pills from last response */}
      {nextStepPills.length > 0 && (
        <div className="px-3 py-2 border-t border-border-subtle flex flex-wrap gap-1.5">
          {nextStepPills.map((pill, i) => (
            <button key={i} disabled={isStreaming}
              onClick={() => handlePillClick(pill)}
              className={`text-[10px] rounded-full px-2.5 py-1 transition-colors disabled:opacity-40 flex items-center gap-1 ${
                pill.action === 'link' ? 'text-text-secondary bg-warm-100 hover:bg-warm-200/80' :
                pill.action === 'need' ? 'text-text-secondary bg-warm-100 hover:bg-warm-200/80' :
                'text-text-muted bg-warm-100 hover:bg-warm-200/80'
              }`}>
              {pill.action === 'link' ? <Link size={9} /> : pill.action === 'need' ? <span className="font-bold">+</span> : <ArrowRight size={9} />}
              {pill.label}
            </button>
          ))}
        </div>
      )}

      {/* Persistent quick actions */}
      {messages.length > 0 && (
        <div className="px-3 py-1.5 flex flex-wrap gap-1">
          {QUICK_PROMPTS.map(qp => (
            <button key={qp.label} onClick={() => handleQuickPrompt(qp.prompt)} disabled={isStreaming || !apiKey}
              className="text-[10px] text-text-muted hover:text-text-secondary bg-warm-50 hover:bg-warm-100 rounded-full px-2 py-0.5 transition-all disabled:opacity-40">
              {qp.label}
            </button>
          ))}
        </div>
      )}

      {/* Input */}
      <div className="px-3 pb-3 pt-1 flex-shrink-0">
        <div className="flex items-center gap-2 border border-border rounded-lg bg-surface px-3 py-2">
          <input type="text" value={input} onChange={e => setInput(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend() } }}
            placeholder={apiKey ? 'Ask the coach...' : 'Set API key in Settings'}
            disabled={!apiKey}
            className="flex-1 text-[13px] bg-transparent text-text placeholder:text-text-muted/50 focus:outline-none" />
          <button onClick={handleSend} disabled={!input.trim() || isStreaming || !apiKey}
            className="text-core disabled:text-warm-400 transition-colors p-0.5">
            <Send size={14} />
          </button>
        </div>
      </div>
    </div>
  )
}

function EmptyState({ onQuickPrompt, disabled }: { onQuickPrompt: (p: string) => void; disabled: boolean }) {
  return (
    <div className="text-text-muted text-[12px] py-6 px-1">
      <p className="text-sm font-semibold text-text font-display mb-1">Problem–Solution Coach</p>
      <p className="mb-3 text-[11px]">Find gaps, suggest links, or generate value propositions.</p>
      <div className="space-y-0.5">
        {QUICK_PROMPTS.map(qp => (
          <button key={qp.label} onClick={() => onQuickPrompt(qp.prompt)} disabled={disabled}
            className="block w-full text-left text-[12px] text-text-secondary border-l-2 border-l-warm-300 pl-2.5 py-1.5 rounded-r hover:bg-warm-50 transition-colors disabled:opacity-40">
            {qp.label}
          </button>
        ))}
      </div>
    </div>
  )
}

function MessageBubble({ message, onAcceptLink, onCreateLink, onFollowUp }: {
  message: CoachMessage
  onAcceptLink: (data: { sourceType: string; sourceId: string; targetType: string; targetId: string }) => void
  onCreateLink: (source: string, target: string) => void
  onFollowUp: (prompt: string) => void
}) {
  if (message.role === 'user') {
    return (
      <div className="flex justify-end">
        <div className="bg-warm-800 text-warm-100 text-[12px] leading-relaxed px-3 py-2 rounded-lg rounded-br-sm max-w-[85%]">
          {message.content}
        </div>
      </div>
    )
  }

  const blocks = parseMarkdownBlocks(message.content)

  return (
    <div className="space-y-1.5">
      {blocks.map((block, i) => {
        if (block.type === 'heading') return (
          <div key={i} className="font-display text-[11px] font-semibold text-text uppercase tracking-wide mt-2 first:mt-0">{block.text}</div>
        )
        if (block.type === 'suggestion') return (
          <button key={i} onClick={() => onCreateLink(block.source!, block.target!)}
            className="flex items-start gap-2 w-full text-left border-l-2 border-l-business pl-2.5 pr-2 py-2 rounded-r hover:bg-warm-50 transition-colors group">
            <Link size={11} className="text-business/60 flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <div className="text-[11px]">
                <span className="font-medium text-text">{block.source}</span>
                <span className="text-text-muted mx-1">→</span>
                <span className="font-medium text-text">{block.target}</span>
              </div>
              {block.rationale && <div className="text-[10px] text-text-muted mt-0.5">{block.rationale}</div>}
            </div>
            <span className="text-[9px] text-text-muted group-hover:text-text-secondary flex-shrink-0 mt-0.5 transition-colors">link</span>
          </button>
        )
        if (block.type === 'bullet') return (
          <div key={i} className="flex items-start gap-1.5 pl-1">
            <span className="text-warm-400 mt-px flex-shrink-0">›</span>
            <span className="text-[12px] text-text-secondary" dangerouslySetInnerHTML={{ __html: inlineMd(block.text) }} />
          </div>
        )
        return <p key={i} className="text-[12px] text-text-secondary" dangerouslySetInnerHTML={{ __html: inlineMd(block.text) }} />
      })}

      {message.suggestedLinks?.map((link, i) => <SuggestedLinkCard key={i} link={link} onAccept={onAcceptLink} />)}
      {message.gaps?.map((gap, i) => (
        <div key={i} className="flex items-start gap-2 border-l-2 border-l-danger/40 pl-2.5 py-1.5 text-[11px] text-text-secondary rounded-r">
          <AlertTriangle size={11} className="text-danger/60 flex-shrink-0 mt-0.5" /><span>{gap.message}</span>
        </div>
      ))}
    </div>
  )
}

// ============================================================================
// Entity name → ID resolution
// ============================================================================

function flattenNodes(nodes: SpaceNode[]): SpaceNode[] {
  const result: SpaceNode[] = []
  function walk(node: SpaceNode) {
    result.push(node)
    node.children.forEach(walk)
  }
  nodes.forEach(walk)
  return result
}

function findBestMatch(nodes: SpaceNode[], name: string): SpaceNode | undefined {
  const lower = name.toLowerCase().trim()
  // Exact match first
  const exact = nodes.find(n => n.title.toLowerCase() === lower)
  if (exact) return exact
  // Contains match
  const contains = nodes.find(n => n.title.toLowerCase().includes(lower) || lower.includes(n.title.toLowerCase()))
  if (contains) return contains
  // Fuzzy: find the node with the most word overlap
  const nameWords = lower.split(/\s+/)
  let best: SpaceNode | undefined
  let bestScore = 0
  for (const node of nodes) {
    const nodeWords = node.title.toLowerCase().split(/\s+/)
    const score = nameWords.filter(w => nodeWords.some(nw => nw.includes(w) || w.includes(nw))).length
    if (score > bestScore && score >= 2) {
      bestScore = score
      best = node
    }
  }
  return best
}

// ============================================================================

function SuggestedLinkCard({ link, onAccept }: {
  link: SuggestedLink
  onAccept: (data: { sourceType: string; sourceId: string; targetType: string; targetId: string }) => void
}) {
  const [dismissed, setDismissed] = useState(false)
  if (dismissed) return null
  return (
    <div className="border-l-2 border-l-business pl-2.5 pr-2 py-2 rounded-r">
      <div className="flex items-center gap-1.5 text-[11px] text-text-secondary font-medium mb-1"><Link size={10} /> Suggested link</div>
      <div className="text-[11px] text-text-secondary mb-2">
        <span className="font-medium text-text">{link.sourceTitle}</span><span className="text-text-muted mx-1">→</span><span className="font-medium text-text">{link.targetTitle}</span>
        {link.rationale && <span className="text-text-muted block mt-0.5 text-[10px]">{link.rationale}</span>}
      </div>
      <div className="flex gap-1.5">
        <button onClick={() => onAccept({ sourceType: link.sourceType, sourceId: link.sourceId, targetType: link.targetType, targetId: link.targetId })}
          className="text-[10px] font-medium text-text-secondary bg-warm-100 hover:bg-warm-200/80 rounded-full px-2.5 py-0.5 transition-colors">Link them</button>
        <button onClick={() => setDismissed(true)}
          className="text-[10px] text-text-muted hover:bg-warm-100 rounded-full px-2.5 py-0.5 transition-colors">Dismiss</button>
      </div>
    </div>
  )
}
